/**
 * 贴纸包整包预览（任务 14.17c；旧版 sticker_pack_preview_sheet.dart 对应）。
 *
 * 入口：聊天里点贴纸气泡（pack_id>0，MessageList 接线）。移动=底部 sheet /
 * 桌面=居中 dialog，同一组件换 CSS（ForwardPicker 同构）。数据走
 * `chat/stickers/packDetail`：篮子命中 Worker 零 HTTP 直出 added=true，未命中
 * GET sticker/pack 恰一次（requests §3 场景 M4）。加/移篮子成功后关闭并由
 * 调用方 toast（篮子变化经 'chat/stickers' 推送回流，面板自动跟上）；失败
 * 留在弹层内提示原样服务端文案（旧版 snackbar「操作失败：…」同口径）。
 */
import { useEffect, useLayoutEffect, useState } from 'preact/hooks';
import { stickersConnection } from '~/store/appWorker';
import { resolveMediaUrl } from '~/chat/mediaUrl';
import { useGlobal } from '~/store/useGlobal';
import { basketPackOf, type CreatedStickerPack } from '~/chat/stickerModel';
import { packDisplayName } from './stickerPacksModel';
import styles from './stickerPackPreview.module.scss';
import { IconClose } from '~/ui/icons';

export function StickerPackPreview(props: {
  packId: number;
  mobile: boolean;
  onClose: () => void;
  /** 加/移成功：调用方展示 toast（弹层已关闭，提示要活得比弹层久） */
  onResult: (text: string) => void;
}) {
  const { packId, onClose, onResult } = props;
  const cdn = useGlobal((s) => s.config?.cdn ?? '');
  const [detail, setDetail] = useState<{ pack: CreatedStickerPack; added: boolean } | null>(null);
  const [failed, setFailed] = useState(false);
  const [loadSeq, setLoadSeq] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setFailed(false);
    void stickersConnection()
      .loadPackDetail(packId)
      .then((d) => {
        if (!alive) return;
        if (d === null) setFailed(true);
        else setDetail(d);
      });
    return () => {
      alive = false;
    };
  }, [packId, loadSeq]);

  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const toggle = (pack: CreatedStickerPack, added: boolean): void => {
    if (busy) return;
    setBusy(true);
    setError('');
    const conn = stickersConnection();
    const op = added ? conn.removePack(pack.id) : conn.addPack(basketPackOf(pack));
    op.then(
      () => {
        onResult(added ? '已移除该表情包' : '已添加到我的表情');
        onClose();
      },
      (e: unknown) => {
        setBusy(false);
        setError(e instanceof Error ? e.message : String(e));
      },
    );
  };

  return (
    <>
      <div
        className={styles.backdrop}
        data-testid="sticker-preview-backdrop"
        onClick={() => {
          if (!busy) onClose();
        }}
      />
      <div
        className={props.mobile ? `${styles.panel} ${styles.mobile}` : styles.panel}
        role="dialog"
        aria-modal="true"
        data-testid="sticker-preview"
      >
        <header className={styles.header}>
          <button
            type="button"
            className={styles.closeBtn}
            data-testid="sticker-preview-close"
            aria-label="关闭"
            disabled={busy}
            onClick={onClose}
          >
            <IconClose class={styles.closeGlyph} />
          </button>
          <div className={styles.titleWrap}>
            {detail !== null && (
              <>
                <span className={styles.title} data-testid="sticker-preview-title">
                  {packDisplayName(detail.pack)}
                </span>
                {detail.pack.author.trim() !== '' && (
                  <span className={styles.subtitle}>@{detail.pack.author.trim()}</span>
                )}
              </>
            )}
          </div>
          <span className={styles.headerSpacer} aria-hidden="true" />
        </header>
        {failed ? (
          <div className={styles.stateBox} data-testid="sticker-preview-failed">
            <span>加载失败</span>
            <button
              type="button"
              className={styles.retryBtn}
              data-testid="sticker-preview-retry"
              onClick={() => setLoadSeq((n) => n + 1)}
            >
              重试
            </button>
          </div>
        ) : detail === null ? (
          <div className={styles.stateBox}>加载中…</div>
        ) : (
          <>
            <div className={styles.grid} data-testid="sticker-preview-grid">
              {detail.pack.items.map((it) => (
                <span key={it.id} className={styles.tile} data-testid="sticker-preview-tile">
                  <img
                    className={styles.tileImg}
                    src={resolveMediaUrl(cdn, it.path)}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </span>
              ))}
            </div>
            <footer className={styles.footer}>
              {error !== '' && (
                <div className={styles.error} data-testid="sticker-preview-error">
                  操作失败：{error}
                </div>
              )}
              {detail.added ? (
                <button
                  type="button"
                  className={styles.removeBtn}
                  data-testid="sticker-preview-remove"
                  disabled={busy}
                  onClick={() => toggle(detail.pack, true)}
                >
                  {busy ? '处理中…' : '移除表情包'}
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.addBtn}
                  data-testid="sticker-preview-add"
                  disabled={busy}
                  onClick={() => toggle(detail.pack, false)}
                >
                  {busy ? '处理中…' : `添加 ${detail.pack.items.length} 个表情`}
                </button>
              )}
            </footer>
          </>
        )}
      </div>
    </>
  );
}
